import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  ApplicationManageData,
  AtoZStatus,
  DocumentStatus,
  JobDetailsModel,
  NheDetails,
  SdaInfo,
} from "../../utility/application-data";
import {
  CandidateInfo,
  CommitUploadRequest,
  CommitUploadResponse,
  DocumentWidget,
  DocumentsListResponse,
} from "../../utility/types/common";
import { isSalesforceApplication } from "../../helpers/url-params";
import {
  DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE,
  DOC_UPLOAD_ITEM_WIDGET_VALID_STATE,
  EDM_REVIEW_STATUS,
  EDM_UPLOAD_STATUS,
} from "../../utility/enums/common";

// TODO: investigate why cannot import helper functions ouside into reduxStore. if imported cause a many unit tests to failed in many places -- assumption: is incorrect webpack setting.
const getDocumentWidgetState = (uploadStatus: EDM_UPLOAD_STATUS) => {
  switch (uploadStatus) {
    case EDM_UPLOAD_STATUS.IN_PROGRESS:
    case EDM_UPLOAD_STATUS.PENDING:
      return DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.PROCESSING;
    case EDM_UPLOAD_STATUS.SUCCEEDED:
      return DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.SUCCESSFUL;
    case EDM_UPLOAD_STATUS.FAILED:
      return DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.FAILED_UPLOAD;
    default:
      throw new Error("Should return a valid state");
  }
};

// TODO: verify all api that need to add x-ash-authorization
// https://code.amazon.com/packages/HvhSimpleHireChecklistUI/blobs/48cb48f55d186d7f118bdef5a4088d3a1cd4b4f1/--/src/helpers/fetcher.ts#L10-L11
// https://code.amazon.com/packages/HvhSimpleHireChecklistServer/blobs/c95d9e6b939784452daf358221825830bce99da9/--/src/api/index.ts#L44-L68

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.NODE_ENV === "development" ? "http://localhost:9000/checklist/" : "/",
  }),
  tagTypes: ["DocumentWidget"],
  endpoints: (builder) => ({
    // ============  GET Requests ==============//
    getAppConfig: builder.query<any, void>({
      query: () => `api/getAppConfig/getASHConfig`,
    }),
    getJobScheduleDetail: builder.query<JobDetailsModel, { applicationId: string; locale: string }>({
      query: ({ applicationId, locale }) =>
        `api/jobcontainer/schedule/${applicationId}/${locale}?includeStartDate=true`,
    }),
    getSalesforceJobDetail: builder.query<JobDetailsModel, { applicationId: string; locale: string }>({
      query: ({ applicationId, locale }) => `api/salesforce/jobdetails/${applicationId}/${locale}`,
    }),
    getUnifiedScheduleDetails: builder.query<JobDetailsModel, { applicationId: string; locale: string }>({
      async queryFn({ applicationId, locale }, _queryApi, _extraOptions, fetchWithBasicQuery) {
        if (!isSalesforceApplication(applicationId)) {
          const JobContainerSchedule = await fetchWithBasicQuery(
            `api/jobcontainer/schedule/${applicationId}/${locale}?includeStartDate=true`
          );
          return { data: JobContainerSchedule.data as JobDetailsModel };
        } else {
          const jobScheduleSF = await fetchWithBasicQuery(`api/salesforce/jobdetails/${applicationId}/${locale}`);
          return { data: jobScheduleSF.data as JobDetailsModel };
        }
      },
    }),
    getNHEAppointment: builder.query<NheDetails, { applicationId: string }>({
      async queryFn({ applicationId }, _queryApi, _extraOptions, fetchWithBasicQuery) {
        if (!isSalesforceApplication(applicationId)) {
          const nheData = await fetchWithBasicQuery(`/api/nhe/appointment/${applicationId}`);
          return { data: nheData.data as NheDetails };
        } else {
          return {
            error: {
              status: 500,
              statusText: "Internal Server Error",
              data: "Should not called nhe appointment for SalesForce",
            },
          };
        }
      },
    }),
    getStartDateInfo: builder.query<SdaInfo, { applicationId: string; locale: string }>({
      query: ({ applicationId, locale }) => `/api/sda/${applicationId}/${locale}`,
    }),
    getApplicationManageData: builder.query<ApplicationManageData, { applicationId: string; locale: string }>({
      query: ({ applicationId, locale }) => `/api/application/application-manage-data/${applicationId}/${locale}`,
    }),
    getApplicationManagementDataFromSF: builder.query<ApplicationManageData, { applicationId: string }>({
      query: ({ applicationId }) => `/api/salesforce/application-manage-data/${applicationId}`,
    }),
    getDocumentStatusInfo: builder.query<
      DocumentStatus,
      { candidateGlobalId: string; applicationId: string; locale: string }
    >({
      query: ({ candidateGlobalId, applicationId, locale }) =>
        `/api/document-status/${candidateGlobalId}/${applicationId}/${locale}/Salesforce/False`,
    }),
    getCandidateAtoZStatus: builder.query<
      AtoZStatus,
      { candidateGlobalId: string; applicationId: string; locale: string; returnResetPasswordUrl: string }
    >({
      query: ({ candidateGlobalId, applicationId, locale, returnResetPasswordUrl }) =>
        `/api/atoz/${candidateGlobalId}/${applicationId}/${locale}/${returnResetPasswordUrl}`,
    }),
    getCandidateData: builder.query<CandidateInfo, { applicationId: string }>({
      query: ({ applicationId }) => `api/cds/candidate/${applicationId}`,
    }),
    getDocumentsWidgets: builder.query<DocumentWidget[], { applicationId: string; bbCandidateId: string }>({
      async queryFn({ applicationId, bbCandidateId }, _queryApi, _extraOptions, fetchWithBasicQuery) {
        // TODO: should remove when endpoint is ready
        if (process.env.NODE_ENV !== "development") {
          return {
            error: {
              status: 500,
              statusText: "Internal Server Error",
              data: "Should not run on prod",
            },
          };
        }

        const documentList = await fetchWithBasicQuery(`/api/edm/documentUpload/${bbCandidateId}/${applicationId}`);

        if (!documentList.error) {
          const { documents } = documentList.data as DocumentsListResponse;
          const documentsRes: DocumentWidget[] = documents.map(
            ({ files, upload_status, review_status, document_id, draft_id, document_type_id }) => {
              return {
                ...files,
                reviewStatus: review_status,
                documentTypeId: document_type_id,
                documentId: document_id,
                draftId: draft_id,
                state: getDocumentWidgetState(upload_status),
              };
            }
          );
          return { data: documentsRes };
        } else {
          return {
            error: {
              status: 500,
              statusText: "Internal Server Error",
              data: documentList.error,
            },
          };
        }
      },
      providesTags: ["DocumentWidget"],
    }),
    commitDocuments: builder.mutation<
      CommitUploadResponse,
      { applicationId: string; candidateGlobalId: string; documents: CommitUploadRequest }
    >({
      query: ({ applicationId, candidateGlobalId, documents }) => ({
        url: `/api/edm/commitUpload/${candidateGlobalId}/${applicationId}`,
        method: "POST",
        body: JSON.stringify({ documents }),
      }),
      invalidatesTags: ["DocumentWidget"],
    }),
    // ============  POST Requests ==============//
    withDrawApplication: builder.mutation<any, { applicationId: string; withdrawReason: string }>({
      query: ({ applicationId, withdrawReason }) => ({
        url: `/api/withdraw-application/${applicationId}`,
        method: "POST",
        body: JSON.stringify({ withdrawReason }),
      }),
    }),
  }),
});

export const {
  useGetAppConfigQuery,
  useGetJobScheduleDetailQuery,
  useGetSalesforceJobDetailQuery,
  useGetUnifiedScheduleDetailsQuery,
  useGetNHEAppointmentQuery,
  useGetStartDateInfoQuery,
  useGetApplicationManageDataQuery,
  useGetApplicationManagementDataFromSFQuery,
  useGetCandidateDataQuery,
  useGetCandidateAtoZStatusQuery,
  useGetDocumentStatusInfoQuery,
  useWithDrawApplicationMutation,
  // doc upload
  useGetDocumentsWidgetsQuery,
  useCommitDocumentsMutation,
} = apiSlice;
